import { DATABASE_MODE_TYPE_ASI } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/recentItemsTypes.js';
import { useRecentItemsQuery } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useRecentItemsQuery.js';
import { useSidebarRecentItemsEligibility } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useSidebarRecentItemsEligibility.js';
import { useSidebarRecentItemsGraphQL } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useSidebarRecentItemsGraphQL.js';
import { useSidebarRecentsFilter } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useSidebarRecentsFilter.js';
import { useRecentThreadUpdates } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useRecentThreadUpdates.js';
import { useLocalThreadRows } from '#pplx/frontend/apps/ask/local-threads/localThreadRows.js';
import { mergeLocalRecentThreads } from '#pplx/frontend/apps/ask/local-threads/mergeLocalRecentThreads.js';
import { canonicalizeLegacyPermalinkPath } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { RECENT_ITEMS_KEY } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { isComputerModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { reportAction } from '#pplx/frontend/metrics/browser.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useGetActivelyStreamingThreads } from '#pplx/frontend/stream/useGetActivelyStreamingThreads.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
/**
 * Applies only the SSE metadata fields that the sidebar actually renders
 * (title, status, unread). Returns the input unchanged when those fields
 * already match.
 */
function applySSEMetadata(item, meta) {
    if (!meta)
        return item;
    const newTitle = meta.title ?? item.title;
    const newStatus = meta.status ?? item.status;
    const newUnread = meta.unread ?? item.unread;
    if (newTitle === item.title && newStatus === item.status && newUnread === item.unread) {
        return item;
    }
    return { ...item, status: newStatus, title: newTitle, unread: newUnread };
}
// The server filters before its row limit. This catches local rows and kept
// rows bridged onto the REST path.
function filterRecentItems(rows, filter) {
    if (filter === 'all')
        return rows;
    return rows.filter((item) => (filter === 'automations') === (item.sidebarItemType === 'automation'));
}
const EMPTY_RECENT_ITEMS = [];
export function buildSidebarRecentItemWithLiveState({ serverItem, sseMetadata, serverSource, hasActiveStream, stableListKey, }) {
    const item = serverSource === 'rest' && serverItem.sidebarItemType !== 'automation'
        ? applySSEMetadata(serverItem, sseMetadata)
        : serverItem;
    const canUseActiveStreamFallback = serverSource === 'rest' && item.sidebarItemType !== 'automation';
    const isStreaming = item.status === 'pending' || (canUseActiveStreamFallback && hasActiveStream);
    return {
        ...item,
        isStreaming,
        listKey: stableListKey,
    };
}
const GHOST_TTL_MS = 30_000;
export function getSidebarRestPathReason({ signedIn, incognito, mobileWeb, filtered, graphqlStatus, }) {
    if (!signedIn)
        return 'signed_out';
    if (incognito)
        return 'incognito';
    if (mobileWeb)
        return 'mobile_web';
    if (filtered)
        return 'filtered';
    if (graphqlStatus === 'error')
        return 'graphql_error';
    return null;
}
// Chooses the server feed for the sidebar list. When a GraphQL transport
// error hands the list to a still-loading REST feed, the last GraphQL rows
// stay on screen so a websocket flap degrades to a static list instead of
// blanking the sidebar while REST warms up.
export function selectSidebarServerFeed({ filter = 'all', recentItemsSource, graphqlStatus, graphqlRecentItems, restRecentItems, isRestRecentItemsLoading, isRestRecentItemsError, }) {
    // REST serves threads only, so it cannot stand in for an automation-only
    // selection: those rows would all be filtered back out.
    if (recentItemsSource === 'graphql' || filter === 'automations') {
        return {
            serverRecentItems: graphqlRecentItems ?? EMPTY_RECENT_ITEMS,
            isServerFeedLoading: graphqlStatus === 'loading',
        };
    }
    // Held while REST loads AND while it has failed with nothing to show: the
    // REST query has retries disabled, so an offline fetch fails within
    // seconds and would otherwise blank the kept rows for the rest of the
    // outage. A genuine empty REST success wins, and a kept-but-empty snapshot
    // (`!= null`, not length) keeps its settled empty state instead of
    // regressing to a shimmer.
    if (graphqlStatus === 'error' &&
        graphqlRecentItems != null &&
        restRecentItems.length === 0 &&
        (isRestRecentItemsLoading || isRestRecentItemsError)) {
        return { serverRecentItems: graphqlRecentItems, isServerFeedLoading: false };
    }
    return { serverRecentItems: restRecentItems, isServerFeedLoading: isRestRecentItemsLoading };
}
export function buildStreamingRecentItem(thread, fallbackUpdatedAt) {
    const stableKey = thread.frontendContextUUID ?? thread.contextUUID ?? thread.id;
    const isComputer = thread.mode?.toUpperCase() === 'ASI' || isComputerModel(thread.displayModel);
    const basePath = isComputer ? '/computer/tasks' : '/search';
    return {
        uuid: thread.id,
        context_uuid: thread.contextUUID ?? thread.id,
        variant: 'thread',
        link: thread.slug != null ? canonicalizeLegacyPermalinkPath(`${basePath}/${thread.slug}`) : '',
        title: thread.title ?? '',
        updated_at: thread.lastUpdated ?? thread.streamCreatedAt ?? fallbackUpdatedAt,
        isStreaming: true,
        unread: false,
        listKey: stableKey,
        ...(isComputer ? { mode_type: DATABASE_MODE_TYPE_ASI, status: 'pending' } : {}),
    };
}
export function useGetRecentItemsWithActiveStreams({ reason, excludeAsi, querySourceFilter, reportGraphQLTelemetry = false, filter: requestedFilter, graphQLSource, }) {
    // opening history can reconnect old entries; only a local submission may
    // optimistically add a row or override the server's task status.
    const activelyStreamingThreads = useGetActivelyStreamingThreads({ includeReconnects: false });
    const queryClient = useQueryClient();
    const { session } = useSession();
    const email = session?.user?.email;
    const { isGraphQLEligible: isClientEligible, signedIn, isIncognito, isMobileStyle, } = useSidebarRecentItemsEligibility(reason);
    const hasGraphQLFilters = Boolean(excludeAsi) || querySourceFilter !== undefined;
    const isGraphQLEligible = (isClientEligible || graphQLSource != null) && !hasGraphQLFilters;
    const { filter: storedFilter } = useSidebarRecentsFilter(isGraphQLEligible);
    const filter = isGraphQLEligible ? (requestedFilter ?? storedFilter) : 'all';
    const legacyGraphQL = useSidebarRecentItemsGraphQL({
        enabled: isGraphQLEligible && graphQLSource == null,
    });
    const { data: graphqlRecentItems, status: graphqlStatus } = graphQLSource ?? legacyGraphQL;
    const recentItemsSource = isGraphQLEligible && graphqlStatus !== 'error' ? 'graphql' : 'rest';
    const restEnabled = recentItemsSource === 'rest' && filter !== 'automations';
    const { data: restRecentItems, isLoading: isRestRecentItemsLoading, isError: isRestRecentItemsError, } = useRecentItemsQuery({
        reason,
        excludeAsi,
        querySourceFilter,
        enabled: restEnabled,
    });
    // Local (on-device) rows only exist in the desktop shell; both feeds above
    // are server feeds and never carry them. Consumers with filters local rows
    // cannot satisfy (excludeAsi, query-source) keep the server list as-is, and
    // incognito keeps its recents-hidden contract — the server feeds return
    // empty there, so the merge must not repopulate the list.
    const localThreadRows = useLocalThreadRows();
    const skipLocalRows = hasGraphQLFilters ||
        isIncognito ||
        (recentItemsSource === 'graphql' && graphqlStatus !== 'active');
    const { serverRecentItems, isServerFeedLoading: isRecentItemsLoading } = selectSidebarServerFeed({
        filter,
        recentItemsSource,
        graphqlStatus,
        graphqlRecentItems,
        restRecentItems,
        isRestRecentItemsLoading,
        isRestRecentItemsError,
    });
    const recentItems = useMemo(() => skipLocalRows
        ? serverRecentItems
        : mergeLocalRecentThreads(serverRecentItems, localThreadRows), [skipLocalRows, serverRecentItems, localThreadRows]);
    const restPathReason = recentItemsSource === 'graphql'
        ? null
        : getSidebarRestPathReason({
            signedIn,
            incognito: isIncognito,
            mobileWeb: isMobileStyle,
            filtered: hasGraphQLFilters,
            graphqlStatus,
        });
    const lastReportedRestPathRef = useRef(null);
    useEffect(() => {
        if (!reportGraphQLTelemetry || restPathReason === null) {
            lastReportedRestPathRef.current = null;
            return;
        }
        if (lastReportedRestPathRef.current === restPathReason) {
            return;
        }
        lastReportedRestPathRef.current = restPathReason;
        reportAction('web.frontend.sidebar_graphql.rest_path_selected', {
            rest_reason: restPathReason,
            surface_reason: reason,
        });
    }, [reason, reportGraphQLTelemetry, restPathReason]);
    // Maps contextUUID/frontendContextUUID → frontendContextUUID for stable
    // Framer Motion keys. The slug and contextUUID change when the backend
    // responds; using frontendContextUUID prevents unmount/remount flicker.
    const optimisticKeyMapRef = useRef(new Map());
    // Bridges the gap between stream completion and server data arrival.
    // Computer task streams reconnect between steps — the thread briefly
    // disappears from activelyStreamingThreads before maybeReconnect() fires.
    // This ref keeps showing the item until server data or a new stream absorbs it,
    // preventing exit→re-enter animation jank.
    const recentlyStreamedRef = useRef(new Map());
    // Invalidate the REST list when any thread transitions to "completed" via SSE,
    // so the next render picks up fresh data from the backend.
    const onStatusCompleted = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: QueryClientHelper.makeQueryKey(RECENT_ITEMS_KEY, email),
        });
    }, [queryClient, email]);
    // REST needs SSE enrichment because its cached snapshot can be stale. The
    // live GraphQL path owns its own metadata, so SSE stays off there; kept
    // GraphQL rows bridged onto the REST path mid-outage are enriched like
    // REST rows (field-compatible), which keeps them fresh until REST takes
    // over.
    const hasPendingItems = useMemo(() => recentItems.some((item) => item.status === 'pending' || item.status === 'blocked' || item.status === 'sleeping'), [recentItems]);
    const threadMetadata = useRecentThreadUpdates({
        enabled: restEnabled && hasPendingItems,
        onStatusCompleted,
    });
    return useMemo(() => {
        const activeStreamIds = new Set(activelyStreamingThreads.map((thread) => thread.id));
        const activeStreamSlugs = new Set(activelyStreamingThreads.filter((thread) => thread.slug != null).map((thread) => thread.slug));
        // Build lookup sets from server data for dedup
        const existingContextUUIDs = new Set(recentItems.map((item) => item.context_uuid).filter(Boolean));
        const existingUUIDs = new Set(recentItems.map((item) => item.uuid));
        const existingSlugs = new Set(recentItems.map((item) => item.link.split('/').pop()));
        // Populate stable key map from streaming threads.
        // Also prune entries whose frontendContextUUID is no longer referenced
        // by any active stream or ghost item, to prevent unbounded growth.
        const activeFrontendUUIDs = new Set();
        for (const st of activelyStreamingThreads) {
            if (st.frontendContextUUID) {
                activeFrontendUUIDs.add(st.frontendContextUUID);
                optimisticKeyMapRef.current.set(st.frontendContextUUID, st.frontendContextUUID);
                if (st.contextUUID) {
                    optimisticKeyMapRef.current.set(st.contextUUID, st.frontendContextUUID);
                }
            }
        }
        for (const cached of recentlyStreamedRef.current.values()) {
            activeFrontendUUIDs.add(cached.item.listKey);
        }
        for (const [key, value] of optimisticKeyMapRef.current) {
            if (!activeFrontendUUIDs.has(value) && !existingContextUUIDs.has(key)) {
                optimisticKeyMapRef.current.delete(key);
            }
        }
        const now = Date.now();
        const streamingItems = activelyStreamingThreads
            .filter((thread) => {
            // Must have some identifier for a stable key
            if (!thread.frontendContextUUID && !thread.contextUUID && !thread.id) {
                return false;
            }
            // Filter out if already in server data
            if (thread.slug != null && existingSlugs.has(thread.slug)) {
                return false;
            }
            if (thread.id && existingContextUUIDs.has(thread.id)) {
                return false;
            }
            if (thread.id && existingUUIDs.has(thread.id)) {
                return false;
            }
            if (thread.contextUUID && existingContextUUIDs.has(thread.contextUUID)) {
                return false;
            }
            return true;
        })
            .map((thread) => buildStreamingRecentItem(thread, new Date(now).toISOString()));
        const serverItems = recentItems.map((item) => {
            const contextUuid = item.context_uuid ?? item.uuid;
            const metadata = threadMetadata.get(contextUuid);
            const isActivelyStreaming = (item.context_uuid ? activeStreamIds.has(item.context_uuid) : false) ||
                activeStreamSlugs.has(item.link.split('/').pop());
            // Use frontendContextUUID-based key if this item was previously a streaming entry
            const mappedKey = item.context_uuid && optimisticKeyMapRef.current.get(item.context_uuid);
            const stableKey = item.sidebarItemType === 'automation' ? item.uuid : (mappedKey ?? item.link);
            return buildSidebarRecentItemWithLiveState({
                serverItem: item,
                sseMetadata: metadata,
                serverSource: recentItemsSource,
                hasActiveStream: isActivelyStreaming,
                stableListKey: stableKey,
            });
        });
        // Track one cached row per streaming thread so stream completion can
        // render a non-streaming placeholder until server data returns the completed item.
        // Use uuid instead of listKey because listKey can change while streaming.
        const currentStreamingKeys = new Set(streamingItems.map((i) => i.listKey));
        const currentStreamingUUIDs = new Set(streamingItems.map((i) => i.uuid));
        for (const item of streamingItems) {
            recentlyStreamedRef.current.set(item.uuid, { item, cachedAt: now });
        }
        // Drop cached rows once server data has the same thread identity.
        const serverKeys = new Set(serverItems.map((i) => i.listKey));
        for (const [key, cached] of recentlyStreamedRef.current) {
            if (serverKeys.has(key) ||
                existingUUIDs.has(cached.item.uuid) ||
                existingContextUUIDs.has(cached.item.uuid) ||
                now - cached.cachedAt > GHOST_TTL_MS) {
                recentlyStreamedRef.current.delete(key);
            }
        }
        // Ghost items: recently-streamed threads not yet in server data or active streams
        const ghostItems = [];
        for (const cached of recentlyStreamedRef.current.values()) {
            if (!currentStreamingKeys.has(cached.item.listKey) &&
                !currentStreamingUUIDs.has(cached.item.uuid)) {
                ghostItems.push({ ...cached.item, isStreaming: false, status: undefined });
            }
        }
        const rows = [...ghostItems, ...streamingItems, ...serverItems];
        return {
            isLoading: isRecentItemsLoading,
            data: filterRecentItems(rows, filter),
        };
    }, [
        recentItems,
        recentItemsSource,
        activelyStreamingThreads,
        isRecentItemsLoading,
        threadMetadata,
        filter,
    ]);
}
//# sourceMappingURL=useGetRecentItemsWithActiveStreams.js.map